import { Box, HStack, Icon, Link, Text } from "@chakra-ui/react"
import Constants from "Constants/shared.json"
import { Button } from "DesignSystem/components"
import { useFeatureFlagLoading } from "Hooks/use-feature-flag"
import { LinkExternalIcon } from "Icons/LinkExternalIcon"
import { LinkIcon } from "Icons/LinkIcon"
import { CardFooter, LegacyCardBody } from "Shared/components/Card/Card"
import { LinkedInIcon } from "Shared/icons/LinkedInIcon"
import { LinkedInDisconnectModal } from "UserCrowd/components/linkedin/LinkedInDisconnectModal"
import { LinkedInVerificationModal } from "UserCrowd/components/linkedin/LinkedInVerificationModal"
import React, { useEffect } from "react"
import { useState } from "react"
import { useLocation, useNavigate } from "react-router"
import {
  useCurrentPanelistInfo,
  useGetPanelistIntegrations,
  useTrackEvent,
} from "~/api/generated/usabilityhub-components"

// ts-prune-ignore-next
export const LINKEDIN_CONNECT_CTA_PARAM = "linkedin_connect_cta"

export const LinkedInSection: React.FC = () => {
  const { mutate: trackEvent } = useTrackEvent({})
  const { enabled: linkedInEnabled, loading: isLoadingLinkedInEnabled } =
    useFeatureFlagLoading("linkedin_panellist_verification")
  const {
    data: panelistIntegrations,
    isLoading: isLoadingPanelistIntegrations,
  } = useGetPanelistIntegrations({}, { enabled: linkedInEnabled })
  const { data: panelistInfo, isLoading: isLoadingPanelistInfo } =
    useCurrentPanelistInfo({}, { enabled: linkedInEnabled })

  const [verifyModalOpen, setVerifyModalOpen] = useState(false)
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false)

  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const linkedinConnectCtaSource = new URLSearchParams(search).get(
    LINKEDIN_CONNECT_CTA_PARAM
  )
  useEffect(() => {
    if (
      !isLoadingPanelistIntegrations &&
      panelistIntegrations &&
      !panelistIntegrations.linkedin &&
      linkedinConnectCtaSource
    ) {
      handleOpenLinkedInVerificationModal()
    }
  }, [
    isLoadingPanelistIntegrations,
    panelistIntegrations,
    linkedinConnectCtaSource,
  ])

  const onVerifyModalClose = () => {
    if (linkedinConnectCtaSource) {
      const params = new URLSearchParams(search)
      params.delete(LINKEDIN_CONNECT_CTA_PARAM)
      navigate(`${pathname}${params.toString() ? `?${params}` : ""}`, {
        replace: true,
      })
    }
    setVerifyModalOpen(false)
  }

  const handleOpenLinkedInVerificationModal = () => {
    setVerifyModalOpen(true)
    trackEvent({
      body: {
        event: "panelist_linkedin_verification_modal_opened",
        event_properties: {
          source: linkedinConnectCtaSource || "settings_profile",
        },
      },
    })
  }

  const handleOpenLinkedInDisconnectModal = () => {
    setDisconnectModalOpen(true)
    trackEvent({
      body: {
        event: "panelist_linkedin_verification_disconnect_modal_opened",
        event_properties: {
          source: "settings_profile",
        },
      },
    })
  }

  if (
    isLoadingPanelistIntegrations ||
    isLoadingLinkedInEnabled ||
    !linkedInEnabled
  ) {
    return <></>
  }

  return (
    <>
      <LegacyCardBody>
        <HStack align="center" justify="space-between">
          <HStack>
            <Box>
              <Icon as={LinkedInIcon} boxSize={5} m={0.5} />
            </Box>
            <Text textStyle="ds.heading.primary">
              {panelistIntegrations?.linkedin
                ? "LinkedIn"
                : "Verify your profile with LinkedIn"}
            </Text>
          </HStack>
          {panelistIntegrations?.linkedin && (
            <Button
              variant="default"
              onClick={handleOpenLinkedInDisconnectModal}
            >
              Disconnect account
            </Button>
          )}
        </HStack>
        <Text textStyle="ds.paragraph.primary">
          {panelistIntegrations?.linkedin
            ? "Your profile is now verified. You're more likely to be selected for relevant, higher-paying studies."
            : "Connect LinkedIn to verify your background and unlock better-paid, more relevant studies."}
          <Link
            textDecoration="none"
            _hover={{ textDecoration: "none" }}
            href={Constants.PANELIST_HELP_CENTER_MANAGING_PROFILE_URL}
            rel="noopener noreferer"
            isExternal
          >
            {" "}
            Learn more <Icon as={LinkExternalIcon} />
          </Link>
        </Text>
        <CardFooter>
          {panelistIntegrations?.linkedin ? (
            !isLoadingPanelistInfo &&
            panelistInfo?.linkedin_profile?.vanity_url && (
              <Link
                variant="noUnderline"
                textStyle="ds.paragraph.primary"
                href={panelistInfo.linkedin_profile.vanity_url}
                rel="noopener noreferer"
                isExternal
              >
                <Icon as={LinkIcon} />{" "}
                {panelistInfo.linkedin_profile.vanity_url}
              </Link>
            )
          ) : (
            <Button
              variant="primary"
              onClick={handleOpenLinkedInVerificationModal}
            >
              Get verified
            </Button>
          )}
        </CardFooter>
      </LegacyCardBody>
      <LinkedInVerificationModal
        isOpen={verifyModalOpen}
        onClose={onVerifyModalClose}
        source="settings_profile"
      />
      <LinkedInDisconnectModal
        isOpen={disconnectModalOpen}
        onClose={() => setDisconnectModalOpen(false)}
        source="settings_profile"
      />
    </>
  )
}
